import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  Spin,
  Button,
  Table,
  Space,
  Drawer,
  Modal,
  message,
} from "antd";
import get from "lodash/get";
import assign from "lodash/assign";
import debounce from "lodash/debounce";
import requireAuth from "../../hocs/requireAuth";
import {
  fetchTemplates,
  deleteTemplate,
  syncTemplateEvents,
  pauseStatusUpdate,
} from "../../redux/actions/templateActions";
import { templateColumns } from "../../constants/tableColumns/templates";
import { DELETE_TEMPLATE_SUCCESS, PAUSE_UPDATE_FAILED, PAUSE_UPDATE_SUCCESS } from "../../constants/message";
import AddEditTemplate from "../../components/template/addEditTemplate";
import { fetchCompanies } from "../../redux/actions/companyActions";
import { SUPER_ADMIN_ROLE_ACCESS } from "../../constants";
import ResyncTemplate from "../../components/template/resyncTemplate";
import HeaderSelector from "../../components/template/headerSelector";
import { fetchConfig } from "../../redux/actions/authActions";
import { replaceMultiple } from "../../utilities/helpers";

const TemplateScreen = () => {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({})
  const inputRef = useRef(null);
  const dispatch = useDispatch();

  const getConfig = () => {
    dispatch(fetchConfig("templates"))
      .then((result) => {
        setConfig(result);
      })
      .catch(() => { });
  };
  const [templateForm, setTemplateForm] = useState({
    visible: false,
    data: null,
    edit: false,
  });
  const [syncForm, setSyncForm] = useState({
    visible: false,
    data: null,
  });
  const [companies, setCompanies] = useState([]);

  const toggleTemplateForm = (data = null, edit = false) => {
    assign(templateForm, {
      visible: !get(templateForm, "visible"),
      data,
      edit,
    });
    setTemplateForm({ ...templateForm });
  };
  const toggleSyncForm = (data = null) => {
    assign(syncForm, {
      visible: !get(syncForm, "visible"),
      data,
    });
    setSyncForm({ ...syncForm });
  };

  const [templates, setTemplates] = useState({
    data: [],
    pagination: null,
    filters: { page: 1, limit: 15, companies: undefined, query: undefined },
  });

  const getTemplates = (page = 1, filters = {}) => {
    setLoading(true);
    const payload = {
      ...filters,
      page,
    };

    if (!get(payload, "query") && inputRef.current) {
      inputRef.current.input.value = "";
      inputRef.current.focus();
    }

    dispatch(fetchTemplates(payload))
      .then((result) => {
        setLoading(false);
        setTemplates({
          data: get(result, "data"),
          pagination: get(result, "meta.pagination"),
          filters: filters,
        });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onSearchTemplate = debounce((filters) => {
    getTemplates(1, filters);
  }, 500);

  const onDeleteTempalte = (id) => {
    setLoading(true);
    dispatch(deleteTemplate(id))
      .then(() => {
        getTemplates(get(templates, "filters.page",1), get(templates, "filters"));
        message.success(DELETE_TEMPLATE_SUCCESS);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getCompanies = () => {
    dispatch(fetchCompanies())
      .then((result) => {
        setCompanies(result);
      })
      .catch(() => { });
  };

  const updatePauseStatus = (uuid,payload)=>{
    setLoading(true)
    dispatch(pauseStatusUpdate(uuid,payload))
    .then((result)=>{
      getTemplates(get(templates, "filters.page",1), get(templates, "filters"))
      setLoading(false)
           message.success(replaceMultiple(PAUSE_UPDATE_SUCCESS,{"{{status}}":get(payload, "status")?"resumed":"paused"}));
    })
    .catch((error)=>{
      setLoading(false)
      message.error(PAUSE_UPDATE_FAILED)
    })
  }

  const refreshEvents = () => {
    setLoading(true);
    dispatch(syncTemplateEvents())
      .then((result) => {
        getTemplates(get(templates, "filters.page",1), get(templates, "filters"));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };
  const isDevMode = process.env.REACT_APP_IS_DEV_MODE === "1";
  useEffect(() => {
    getConfig()
    getTemplates(get(templates, "filters.page",1), get(templates, "filters"));
    getCompanies();
  }, []);
  
  return (
    <Spin spinning={loading}>
      <div className="pl-2 py-2 h-full flex flex-col">
        <div className="flex flex-row justify-between pb-4">
          <div>
            <p className="text-xl font-medium leading-[30px] text-black-1">
              Templates
            </p>
          </div>
          <div>
            <Space>
              {isDevMode && (
                <Button
                  type="primary"
                  onClick={() => {
                    refreshEvents();
                  }}
                >
                  Refresh Event
                </Button>
              )}
              <Button
                type="primary"
                onClick={() => {
                  toggleTemplateForm();
                }}
              >
                Add Template
              </Button>
            </Space>
          </div>
        </div>

        <div className="h-full rounded-xl overflow-hidden bg-white flex-1 overflow-y-auto scrollbar-hidden pb-5 min-w-full ">
          <div className="w-full px-8 py-6">
            <div className="justify-end flex items-center h-full space-x-5 ">
              <HeaderSelector companies={companies} config={config} getTemplates={getTemplates} onSearchTemplate={onSearchTemplate} templates={templates} inputRef={inputRef}/>
            </div>
          </div>
          <div className="px-8 overflow-y-auto">
            <Table
              columns={templateColumns(
                toggleTemplateForm,
                onDeleteTempalte,
                toggleSyncForm,
                updatePauseStatus
              )}
              dataSource={get(templates, "data")}
              pagination={{
                pageSize: get(templates, "pagination.per_page", 1),
                current: get(templates, "pagination.current_page", 1),
                total: get(templates, "pagination.total", 1),
                onChange: (val) => {
                  getTemplates(val, { ...get(templates, "filters") });
                },
              }}
             rowKey={(record) => get(record, "id") || crypto.randomUUID()}
            />
          </div>
        </div>
        <Modal
          title={`Resync ${get(syncForm, "data.name")} Event stats`}
          open={get(syncForm, "visible")}
          footer={null}
          onCancel={() => {
            toggleSyncForm();
          }}
          destroyOnHidden
          centered
        >
          <ResyncTemplate
            template={get(syncForm, "data")}
            toggle={toggleSyncForm}
            refresh={()=>getTemplates(get(templates, "filters.page",1), get(templates, "filters"))}
          />
        </Modal>
        <Drawer
          destroyOnHidden={true}
          title={get(templateForm, "edit") ? "Edit Template" : "Add Template"}
          width={"90%"}
          classNames={"bg-yellow-100"}
          onClose={() => {
            toggleTemplateForm();
          }}
          height
          open={get(templateForm, "visible")}
        >
          <AddEditTemplate
            refresh={()=>getTemplates(get(templates, "filters.page",1),get(templates, "filters"))}
            toggle={toggleTemplateForm}
            data={get(templateForm, "data")}
            edit={get(templateForm, "edit")}
            companies={companies}
          />
        </Drawer>
      </div>
    </Spin>
  );
};

export default requireAuth(TemplateScreen, SUPER_ADMIN_ROLE_ACCESS);
